import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { useDocument } from '@tribeplatform/react-ui-kit/hooks'
import { ThemeProvider as UiKitThemeProvider } from '@tribeplatform/react-ui-kit/ThemeProvider'

import { useAppearance } from './AppearanceProvider'
import { BASE_THEME } from './base.theme'
import { ThemeConvertor } from './theme-convertor'
import { Theme } from './types'
import { applyTheme } from './utils'

export type ThemeProps = {
  theme: Theme
}

export const ThemeContext = createContext<ThemeProps>({
  theme: BASE_THEME,
})

export const useTheme = (): ThemeProps => {
  const context = useContext(ThemeContext)
  return context
}

export type ThemeProviderProps = {
  customTheme?: Theme
  customDarkMode?: boolean
  children?: ReactNode
}

/**
 * @deprecated will be replaced with UiKitThemeProvider after typography migration
 */
export const ThemeProvider: FC<ThemeProviderProps> = ({
  children,
  customTheme,
  customDarkMode,
}) => {
  const {
    data: { network },
  } = useAuthToken()
  const { darkMode: defaultDarkMode } = useAppearance()
  const { document } = useDocument()

  const darkMode = customDarkMode || defaultDarkMode
  const theme = useMemo(() => {
    return (
      customTheme ||
      new ThemeConvertor({
        base: BASE_THEME,
        networkOldTheme: network?.themes?.active,
        networkTheme: network?.activeTheme,
      }).toTheme()
    )
  }, [customTheme, network?.activeTheme, network?.themes?.active])

  useEffect(() => {
    applyTheme(document, theme)
  }, [document, theme])

  const themeObject = useMemo(() => {
    return {
      id: theme.id,
      name: theme.name ?? '',
      colors: theme.newColors,
    }
  }, [theme.id, theme.name, theme.newColors])

  return (
    <UiKitThemeProvider
      theme={themeObject}
      colorScheme={darkMode ? 'dark' : 'light'}
    >
      <ThemeContext.Provider
        value={{
          theme,
        }}
      >
        {children}
      </ThemeContext.Provider>
    </UiKitThemeProvider>
  )
}
